import {makeAutoObservable, runInAction} from 'mobx'
import {Time} from 'yougile-util'
import {DataObjectId, Hub, newsCompanyId} from 'yougile-model'
import {Storage} from '@client/storage'
import {Sync, syncLoadListener} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {Tracking, TrackingEvent} from '@common/tracking'
import {ChatUtils} from '@extensions/chat-utils'
import {News, NewsSeenReason} from '@extensions/news'
import {PageService} from '@extensions/page-service'

interface NewsItem {
  id: DataObjectId
  title: string
  date: string
  messages: number
  htmlContent: string
  users: DataObjectId[]
}

interface State {
  newsItem: NewsItem | undefined
}

const loadTime = Time.now()
const maxWaitTime = 20_000

export class NewsPopupStore {
  state: State = {
    newsItem: undefined
  }

  constructor() {
    makeAutoObservable(this)
    this.computeNewsItem()
    const update = () => {
      if (Time.now() > loadTime + maxWaitTime) {
        return
      }
      runInAction(() => {
        this.computeNewsItem()
      })
    }
    syncLoadListener.addListener(update)
    SyncEvents.addListener(newsCompanyId, '*', update)
  }

  goToNewsItem(): void {
    const newsHub = this.getHub()
    if (!newsHub) {
      return
    }
    this.close()
    PageService.goTo('feedback')
    ChatUtils.openChat(newsHub)
  }

  close(): void {
    const hub = this.getHub()
    if (hub) {
      News.markClosed(hub)
      this.state.newsItem = undefined
    }
  }

  private getHub(): Hub | undefined {
    return this.state.newsItem
      ? Sync.newsSandbox?.getHub(this.state.newsItem.id)
      : undefined
  }

  private computeNewsItem() {
    const allNews = News.list()
    const newsWithPopup = allNews
      .sort((a, b) => b.timestamp - a.timestamp)
      .filter(n => News.isWithPopup(n))
    const latestWithPopup = newsWithPopup[0]
    const devAlwaysShowLatestPopup =
      Storage.getItem('dev_always_show_latest_news_popup') === 'true'
    const isUnseenReason =
      latestWithPopup && News.isUnseenReason(latestWithPopup)
    const isUnseen = isUnseenReason === NewsSeenReason.Unseen
    const isClosed = latestWithPopup && News.isClosed(latestWithPopup)
    const newsToShowHub =
      latestWithPopup && isUnseen && !isClosed ? latestWithPopup : undefined
    const newsToShow = devAlwaysShowLatestPopup
      ? latestWithPopup
      : newsToShowHub
    const isNewsToShowHubNew =
      newsToShow && newsToShow.id !== this.state.newsItem?.id
    const messages = newsToShow ? newsToShow.chat.getMessages() : []
    const users: DataObjectId[] = [...new Set(messages.map(m => m.from))]

    if (isNewsToShowHubNew) {
      this.state.newsItem = newsToShow
        ? {
            id: newsToShow.id,
            date: News.getDateString(newsToShow),
            title: newsToShow.title,
            htmlContent: newsToShow.description,
            messages: newsToShow.chat.getMessages().length,
            users: users
          }
        : undefined

      void Tracking.send(TrackingEvent.newsPopupShowed)
    }
  }
}
